import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, ClipboardList, CreditCard, LogOut, Menu, X, MessageCircle, CalendarDays, ShoppingBag, Phone, Gift, Newspaper, MapPin, Bell, Users, Shield, BookOpen } from 'lucide-react';
import { useState } from 'react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dd7e4899-b441-40b2-8a4d-ff26ed1613bb/artifacts/tc6miqnj_1000017045.jpg";

export default function Navbar() {
  const { user, logout, isTrialUser, trialDaysRemaining, hasFullAccess } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/barn-board', label: 'Barn Board', icon: Newspaper },
    { to: '/calendar', label: 'Calendar', icon: CalendarDays },
    { to: '/barn-chores', label: 'Chores', icon: ClipboardList },
    { to: '/events', label: 'Events', icon: MapPin },
    { to: '/marketplace', label: 'Market', icon: ShoppingBag },
    { to: '/emergency', label: 'Emergency', icon: Phone },
    { to: '/chat', label: 'Chat', icon: MessageCircle },
    { to: '/referral', label: 'Refer', icon: Gift },
    { to: '/reminders', label: 'Reminders', icon: Bell },
    { to: '/friends', label: 'Friends', icon: Users },
    { to: '/providers', label: 'Directory', icon: BookOpen },
    { to: '/subscription', label: 'Plan', icon: CreditCard },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-lg bg-[#F2F7F4]/90 border-b border-[#D1E0D5]" data-testid="navbar">
      {/* Trial Banner */}
      {isTrialUser && (
        <div className="bg-[#2D6A4F] text-white text-center py-1.5 text-xs font-medium" data-testid="trial-banner">
          <span className="mr-1">Your free month:</span>
          <span className="font-bold">{trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} left</span>
          <span className="mx-2">|</span>
          <span className="text-white/70">Subscribe now for $9.95/mo (1st year rate)</span>
          <span className="mx-2">|</span>
          <Link to="/subscription" className="underline font-semibold hover:text-[#A7C4BC]">Lock in rate</Link>
        </div>
      )}
      {!hasFullAccess && !isTrialUser && user && (
        <div className="bg-[#8B4513] text-white text-center py-1.5 text-xs font-medium" data-testid="expired-banner">
          Your free month has ended.
          <Link to="/subscription" className="underline font-semibold ml-2 hover:text-[#F9F7F3]">Subscribe for $14.95/mo to unlock all features</Link>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="Stable Solutions" className="h-10 w-10 rounded-full object-cover shadow-[0_4px_12px_rgb(27,67,50,0.08)]" />
            <div className="hidden sm:block">
              <span className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1B4332] leading-tight block">Stable Solutions</span>
              <span className="text-[10px] font-medium tracking-wider text-[#4B6B58] uppercase">The Oaks Legacy Ranch</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-2">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium btn-lift ${
                  isActive(link.to)
                    ? 'bg-[#1B4332] text-white shadow-[0_4px_12px_rgb(27,67,50,0.15)]'
                    : 'text-[#4B6B58] hover:bg-[#E9EFE8]'
                }`}
              >
                <link.icon size={16} />
                {link.label}
              </Link>
            ))}
            {user?.is_admin && (
              <Link to="/admin" data-testid="nav-admin" className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium btn-lift ${isActive('/admin') ? 'bg-[#1B4332] text-white shadow-[0_4px_12px_rgb(27,67,50,0.15)]' : 'text-[#7C3AED] hover:bg-[#F3E8FF]'}`}>
                <Shield size={16} /> Admin
              </Link>
            )}
          </div>

          {/* User & Logout */}
          <div className="hidden md:flex items-center gap-4">
            <span className="text-sm text-[#4B6B58] font-medium" data-testid="user-name">{user?.name}</span>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-[#4B6B58] hover:bg-[#E9EFE8] btn-lift"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-xl text-[#1B4332]"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 animate-fade-in" data-testid="mobile-menu">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${
                  isActive(link.to) ? 'bg-[#1B4332] text-white' : 'text-[#4B6B58]'
                }`}
              >
                <link.icon size={18} />
                {link.label}
              </Link>
            ))}
            {user?.is_admin && (
              <Link to="/admin" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-admin" className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${isActive('/admin') ? 'bg-[#1B4332] text-white' : 'text-[#7C3AED]'}`}>
                <Shield size={18} /> Admin Dashboard
              </Link>
            )}
            <button
              onClick={() => { logout(); setMobileOpen(false); }}
              data-testid="mobile-logout-button"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-[#4B6B58] w-full"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
